import React from 'react'
import { Box, styled } from '@mui/material'
import { AvatarStack, AvatarStackItem } from '@/design-system'

const Bar = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 82,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 22,
  padding: '0 36px',
  background: 'color-mix(in srgb, var(--ds-night-ink) 7%, transparent)',
  backdropFilter: 'blur(22px) saturate(1.4)',
  WebkitBackdropFilter: 'blur(22px) saturate(1.4)',
  borderTop: '1px solid color-mix(in srgb, var(--ds-night-ink) 14%, transparent)',
  flexWrap: 'wrap',
})

const MutualCluster = styled(Box)({ display: 'flex', alignItems: 'center', gap: 10 })

const MutualLink = styled('button')({
  fontSize: 12.5,
  fontWeight: 600,
  color: 'color-mix(in srgb, var(--ds-night-ink) 82%, transparent)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  '&:hover': { color: 'var(--ds-accent-dot-4)', textDecoration: 'underline' },
})

const GbDiv = styled(Box)({ width: 1, height: 26, background: 'color-mix(in srgb, var(--ds-night-ink) 16%, transparent)' })

const Stats = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 30,
  marginLeft: 'auto',
})

const Stat = styled(Box)({
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover .stat-v': { color: 'var(--ds-accent-dot-4)' },
})

const StatV = styled('div')({
  fontSize: 19,
  fontWeight: 750,
  letterSpacing: '-0.03em',
  lineHeight: 1,
  color: 'var(--ds-night-ink)',
  transition: 'color 140ms ease',
})

const StatL = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.08em',
  color: 'color-mix(in srgb, var(--ds-night-ink) 50%, transparent)',
  marginTop: 4,
})

export interface ProfileGlassBarStat {
  value: number
  label: string
  onClick?: () => void
}

export interface ProfileGlassBarProps {
  mutuals?: AvatarStackItem[]
  mutualCount?: number
  onMutualsClick?: () => void
  stats: ProfileGlassBarStat[]
}

function fmt(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

export function ProfileGlassBar({ mutuals, mutualCount, onMutualsClick, stats }: ProfileGlassBarProps) {
  const showMutuals = mutuals && mutuals.length > 0 && !!mutualCount

  return (
    <Bar>
      {showMutuals && (
        <>
          <MutualCluster>
            <AvatarStack items={mutuals!} size={22} max={3} />
            <MutualLink onClick={onMutualsClick}>{mutualCount} mutual connection{mutualCount === 1 ? '' : 's'}</MutualLink>
          </MutualCluster>
          <GbDiv />
        </>
      )}
      <Stats>
        {stats.map((s) => (
          <Stat key={s.label} onClick={s.onClick}>
            <StatV className='stat-v'>{fmt(s.value)}</StatV>
            <StatL>{s.label}</StatL>
          </Stat>
        ))}
      </Stats>
    </Bar>
  )
}
